import { motion } from "framer-motion"
import { Link } from "react-router-dom"
import {ArrowRight, Clock, Tag, Sparkles} from 'lucide-react'
import { useArticles } from "@/hooks/useArticles"

export default function LatestBling() {
  const { articles, loading, error } = useArticles({ publishedOnly: true, limit: 3 })

  return (
    <section className="relative py-20 sm:py-28 bg-black/40">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-12 gap-4"
        >
          <div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight mb-3"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              <span className="text-white">Latest </span>
              <span className="gold-shimmer">Bling</span>
            </h2>
            <p className="text-gray-500 text-lg">
              Fresh drops, guides, and prestige skin news.
            </p>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-yellow-500 hover:text-yellow-400 font-semibold text-sm uppercase tracking-wider transition-colors group"
          >
            View All Posts
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[0, 1, 2].map((item) => (
              <div key={item} className="h-72 rounded-2xl border border-gray-800/50 bg-gray-950 animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12 rounded-2xl border border-red-900/40 bg-red-950/20">
            <p className="text-red-300">{error}</p>
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12 rounded-2xl border border-gray-800/50 bg-gray-950">
            <Sparkles className="w-10 h-10 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500">No published articles yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {articles.map((article, index) => (
              <motion.article
                key={article._id || article.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
                className="card-lift group rounded-2xl overflow-hidden border border-gray-800/50 hover:border-yellow-700/40 bg-gray-950 transition-all"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={article.image || ""}
                    alt={article.alt || article.title || "Bling article"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent" />
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-500/20 border border-yellow-600/30 text-yellow-400 text-xs font-semibold uppercase tracking-wider">
                      <Tag className="w-3 h-3" />
                      {article.category}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-2 text-gray-600 text-xs mb-3">
                    <Clock className="w-3.5 h-3.5" />
                    {article.date}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {article.excerpt}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
